import localForage from "localforage";
import { put, takeEvery } from "redux-saga/effects";

import { NOTIFICATION_STATUS } from "App/componentsConfig";
import { appInstance, appInstanceV2 } from "axios-config";
import { config } from "config";
import { HELPERS, HTTP_EXCEPTION_MESSAGES, NOTIFICATIONS } from "consts";
import {
  fetchFRRecoveryPointsFailure,
  fetchFRRecoveryPointsSuccess,
  fetchFilesFailure,
  fetchFilesSuccess,
  removeRestoreURLLoader,
  showNotification,
  showNotificationWithTimeout,
  storeRequestParams,
  updateFRRecoveryPointSuccess
} from "store/actions";
import {
  decompressionAlgorithm,
  decryptData,
  decryptDataArray,
  encryptDataArray,
  localDownload,
  waitForConnection
} from "utils";
import * as types from "./types";

export function* fetchFRRecoveryPointsStart(action) {
  const params = {
    clientUUID: action.clientUUID,
    restoreType: action.restoreType,
    loginId: decryptData(localStorage.getItem("mirrorId")),
    srpoint: action.params.serverIPAddress ? action.params.serverIPAddress : "",
    ...(action.scheduleUUID && { scheduleUUID: action.scheduleUUID })
  };
  yield put(storeRequestParams(params));
  const storageKey =
    params.clientUUID + (action.scheduleUUID ? action.scheduleUUID : "");
  const local = { present: false, data: "" };

  yield localForage.getItem(storageKey).then(v => {
    if (v) {
      local.present = true;
      local.data = v;
    }
  });

  if (local.present) {
    yield put(updateFRRecoveryPointSuccess(JSON.parse(local.data)));
  } else {
    try {
      const response = yield appInstance.get("/restore/recoveryPoints", {
        params: params
      });
      if (response.status === 200) {
        if (action.scheduleUUID) {
          yield put(updateFRRecoveryPointSuccess(response.data));
          yield localForage.setItem(
            action.clientUUID + action.scheduleUUID,
            JSON.stringify(response.data)
          );
        } else {
          yield put(
            fetchFRRecoveryPointsSuccess(response.data, {
              requestConfigParams: response.config.params
            })
          );
        }
      } else {
        if (action.scheduleUUID) {
          yield put(
            showNotificationWithTimeout("restore", {
              status: NOTIFICATION_STATUS.failure.name,
              msg:
                (HTTP_EXCEPTION_MESSAGES[response.data]
                  ? HTTP_EXCEPTION_MESSAGES[response.data]
                  : response.data) +
                " (" +
                response.status +
                ")",
              httpError: true
            })
          );
        } else {
          yield put(
            fetchFRRecoveryPointsFailure(
              {
                status: response.status,
                msg: response.data
              },
              {
                requestConfigParams: response.config.params
              }
            )
          );
        }
      }
    } catch (error) {
      console.log(error);
    }
  }
}

export function* fetchUpdatedVolumesFR(params) {
  yield takeEvery(types.FETCH_UPDATED_VOLUMES_FR, action => {
    const req = {
      action: "updatedVolumeList",
      data: { clientUUID: action.clientUUID }
    };

    waitForConnection(params.wsConnection, req, 1000);
  });
}

export function* fetchFilesStart(action) {
  const params = {
    clientUUID: action.params.clientUUID,
    loginId: decryptData(localStorage.getItem("mirrorId"))
  };
  const body = {
    scheduleUUID: action.params.scheduleUUID,
    volumeSerial: action.params.volume.volumeSerialNumber,
    path: action.params.path,
    ...(action.params.file
      ? action.params.file.fileId && { fileID: action.params.file.fileId }
      : {}),
    ...(action.params.offset ? { offset: action.params.offset } : {})
  };

  const storageKey =
    params.clientUUID +
    body.scheduleUUID +
    body.volumeSerial +
    (body.fileID ? body.fileID : "");
  const local = { present: false, data: "" };

  yield localForage.getItem(storageKey).then(v => {
    if (v) {
      local.present = true;
      local.data = v;
    }
  });

  if (local.data.offset !== undefined && local.data.offset >= body.offset)
    body.offset = local.data.offset + 100;

  yield put(storeRequestParams({ ...params, ...body }));
  if (
    local.present &&
    (body.offset
      ? local.data.offset !== undefined && local.data.offset === body.offset
      : true)
  ) {
    yield put(
      fetchFilesSuccess(decryptDataArray(local.data.files), {
        volume: action.params.volume,
        file: action.params.file,
        type: "fetch",
        path: action.params.path,
        requestConfigParams: { ...params, ...body },
        responseStatus: JSON.parse(local.data.status)
      })
    );
  } else {
    try {
      const response = yield appInstanceV2.post(
        "/restore/filesList?srpoint=" +
          (action.params.serverIPAddress ? action.params.serverIPAddress : ""),
        body,
        {
          params: params,
          responseType: "arraybuffer",
          "Accept-Encoding": "gzip",
          decompress: true
        }
      );

      if (response.status === 200 || response.status === 206) {
        if (body.offset) {
          yield put(
            fetchFilesSuccess(
              decryptDataArray(local.data.files).concat(
                decompressionAlgorithm(response.data)
              ),
              {
                volume: action.params.volume,
                file: action.params.file,
                type: "fetch",
                path: action.params.path,
                requestConfigParams: {
                  ...response.config.params,
                  ...JSON.parse(response.config.data)
                },
                responseStatus: response.status,
                concat: true
              }
            )
          );
          yield localForage.getItem(storageKey).then(function(item) {
            item.files = decryptDataArray(item.files).concat(
              decompressionAlgorithm(response.data)
            );
            item.status = response.status;
            item.offset = body.offset;
            // Encrypt only the files property before storing it back
            item.files = encryptDataArray(item.files);
            localForage.setItem(storageKey, item);
          });
        } else {
          yield put(
            fetchFilesSuccess(decompressionAlgorithm(response.data), {
              volume: action.params.volume,
              file: action.params.file,
              type: "fetch",
              path: action.params.path,
              requestConfigParams: {
                ...response.config.params,
                ...JSON.parse(response.config.data)
              },
              responseStatus: response.status
            })
          );

          yield localForage.setItem(storageKey, {
            files: encryptDataArray(decompressionAlgorithm(response.data)),
            status: response.status
          });
        }
      } else {
        yield put(fetchFilesFailure(response.data));
        yield put(
          showNotificationWithTimeout("restore", {
            status: NOTIFICATION_STATUS.failure.name,
            msg:
              (HTTP_EXCEPTION_MESSAGES[response.data]
                ? HTTP_EXCEPTION_MESSAGES[response.data]
                : response.data) +
              " (" +
              response.status +
              ")",
            httpError: true
          })
        );
      }
    } catch (error) {
      console.log(error);
    }
  }
}

export function* initiateRestore(params) {
  yield takeEvery(types.INITIATE_RESTORE, action => {
    const req = {
      action: "startRestore",
      data: action.data
    };

    waitForConnection(params.wsConnection, req, 1000);
  });
}

export function* patternSearch(action) {
  const params = {
    clientUUID: action.search.clientUUID,
    loginId: decryptData(localStorage.getItem("mirrorId")),
    srpoint: action.search.serverIPAddress ? action.search.serverIPAddress : ""
  };

  const body = {
    scheduleUUID: action.search.scheduleUUID,
    volumeSerial: action.search.volume.volumeSerialNumber,
    ...(action.search.folder
      ? action.search.folder.fileId && {
          folderID: action.search.folder.fileId
        }
      : {}),
    pattern: action.search.pattern,
    path: action.search.path
  };
  yield put(storeRequestParams({ ...params, ...body }));
  const local = { present: false, data: "" };

  const storageKey =
    params.clientUUID +
    body.scheduleUUID +
    body.volumeSerial +
    (body.folderID ? body.folderID : "") +
    body.pattern;
  yield localForage.getItem(storageKey).then(v => {
    if (v) {
      local.present = true;
      local.data = v;
    }
  });
  if (local.present) {
    yield put(
      fetchFilesSuccess(
        body.pattern !== ""
          ? JSON.parse(local.data.files)
          : decryptDataArray(local.data.files),
        {
          volume: action.search.volume,
          file: {
            fileId: action.search.folder
              ? action.search.folder.fileId
              : undefined,
            name: HELPERS.slidingPopup.searchResult,
            searchActions: { ...action.search }
          },
          type: "search",
          requestConfigParams: { ...params, ...body },
          responseStatus: JSON.parse(local.data.status)
        }
      )
    );
  } else {
    try {
      const response = yield appInstance.post("/restore/filesSearch", body, {
        params: params
      });

      if (response.status === 200 || response.status === 206) {
        yield put(
          fetchFilesSuccess(response.data, {
            volume: action.search.volume,
            file: {
              fileId: action.search.folder
                ? action.search.folder.fileId
                : undefined,
              name: HELPERS.slidingPopup.searchResult,
              searchActions: { ...action.search }
            },
            type: "search",
            requestConfigParams: {
              ...response.config.params,
              ...JSON.parse(response.config.data)
            },
            responseStatus: response.status
          })
        );
        yield localForage.setItem(storageKey, {
          files: JSON.stringify(response.data),
          status: response.status
        });
      } else {
        yield put(fetchFilesFailure(response.data));
        yield put(
          showNotificationWithTimeout("restore", {
            status: NOTIFICATION_STATUS.failure.name,
            msg:
              (HTTP_EXCEPTION_MESSAGES[response.data]
                ? HTTP_EXCEPTION_MESSAGES[response.data]
                : response.data) +
              " (" +
              response.status +
              ")",
            httpError: true
          })
        );
      }
    } catch (error) {
      console.log(error);
    }
  }
}

export function* downloadRestoreFilesStart(action) {
  try {
    const response = yield appInstance.post(
      "/restore/filesDownload",
      action.params,
      {
        params: {
          clientUUID: action.clientUUID,
          srpoint: action.serverIPAddress ? action.serverIPAddress : ""
        }
      }
    );

    if (response.status === 200) {
      localDownload(response.data);
      yield put(removeRestoreURLLoader(action.params.fileIds[0]));
    } else {
      yield put(removeRestoreURLLoader(action.params.fileIds[0]));
      yield put(
        showNotification("restore", {
          status: NOTIFICATION_STATUS.failure.name,
          msg: NOTIFICATIONS.restore.unableToDownload
        })
      );
    }
  } catch (error) {
    yield put(removeRestoreURLLoader(action.params.fileIds[0]));
    yield put(
      showNotification("restore", {
        status: NOTIFICATION_STATUS.failure.name,
        msg: NOTIFICATIONS.restore.unableToDownload
      })
    );
    console.log(error);
  }
}

export function* enumFolder(params) {
  yield takeEvery(types.ENUM_FOLDER, action => {
    const req = {
      action: "enumFolder",
      data: action.data
    };

    waitForConnection(params.wsConnection, req, 1000);
  });
}

export function* createFolder(params) {
  yield takeEvery(types.CREATE_FOLDER, action => {
    const req = {
      action: "createFolder",
      data: action.data
    };

    waitForConnection(params.wsConnection, req, 1000);
  });
}

export function* downloadRestoreISOStart(params) {
  try {
    /*     let file_path =
      config.API_URL +
      (params.client ? "/" + params.client : "") +
      "/static/" +
      (params.client ? "download/" : "") +
      "recoveryBuilder?loginId=" +
      decryptData(localStorage.getItem("mirrorId")) +
      "&token=" +
      localStorage.getItem("token"); */

    let file_path = "";
    if (params.client === "idrive") {
      file_path =
        "https://api.idrivemirror.com/api/v1/static/downloadBuilder/MirrorRestoreMediaBuilderInstaller.exe";
    } else if (params.client === "ibackup") {
      file_path =
        config.API_URL +
        (params.client ? "/" + params.client : "") +
        "/static/" +
        (params.client ? "download/" : "") +
        "recoveryBuilder?loginId=" +
        decryptData(localStorage.getItem("mirrorId")) +
        "&token=" +
        localStorage.getItem("token");
    } else {
      file_path =
        "https://api.idrivemirror.com/api/v1/static/downloadBuilder/MirrorRestoreMediaBuilderInstaller_mirror.exe";
    }

    const a = document.createElement("A");

    a.href = file_path;
    a.download = file_path.substr(file_path.lastIndexOf("/") + 1);

    yield document.body.appendChild(a);
    yield a.click();
    yield document.body.removeChild(a);
  } catch (error) {
    console.log(error);
  }
}
